import React, { useState, useRef, useMemo } from 'react';
import { LoadingSpinner, PitcherOverviewAttack2KHeatMaps } from 'yankees-react-utils';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
    Advance, Player, PlayerReport, useOktaQuery, useCachedOktaQuery, isLhb as isLhbFunc,
    HeatMaps as HeatMapsApi, Stats,
} from '../../../../data';
import ReportCard from '../../../../components/ReportCard';
import PitcherHeader from '../PitcherHeader';
import Filters from './Filters';
import DeliveryNotes from './DeliveryNotes';
import RepertoireTable from './RepertoireTable';
import PitchFrequencyTable from './PitchFrequencyTable';
import AdvanceReportNotes from './AdvanceReportNotes';
import PitchLocations from './PitchLocations';
import HeatMaps from './HeatMaps';
import PitchBreak from './PitchBreak';
import TrendsChart from './TrendsChart';
import PitchUsageTable from './PitchUsageTable';
import BarrelBoxes from './BarrelBoxes';
import AttackPlanNotes from './AttackPlanNotes';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import useConfig from '../../../../utils/useConfig';
import useOpposingTeamSelector from '../../../../utils/useOpposingTeamSelector';
import DamageZones from './DamageZones';
import SprayChart from './SprayChart';
import PitchMovementImages from './PitchMovementImages';
import StatNotes from './StatNotes';
import BatTracking from './BatTracking';

const StyledContainer = styled.div`
    width: 95%;
    margin: 12px auto;

    .card-body h4.clearfix {
        text-transform: uppercase;
    }

    .header-wrapper {
        display: flex;
    }

    ul {
        list-style: none;
    }
`;

const PitcherOverview = ({ pitcherId, batter: batterOverride = null, userTeamId = null, initialHandedness = null }) => {
    const { t: translate } = useTranslation();
    const { UsePreviousYearData } = useConfig();
    const modalContainer = useRef(null);

    const selectedPlayer = usePlayerSelector();
    const batter = batterOverride || selectedPlayer;
    const opposingTeam = useOpposingTeamSelector();
    const resolvedUserTeamId = userTeamId || selectedPlayer.TeamID;

    const [year, setYear] = useState(null);
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear() - (UsePreviousYearData ? 1 : 0), 0, 1));
    const [endDate, setEndDate] = useState(new Date());
    const [handedness, setHandedness] = useState(null);
    const [pitcherLevelCodes, setPitcherLevelCodes] = useState({ [pitcherId]: opposingTeam.LevelID !== 8 ? -2 : -1 });

    const resolvedLevelcode = useMemo(() => pitcherLevelCodes[pitcherId] ?? (opposingTeam.LevelID !== 8 ? -2 : -1), [pitcherId, pitcherLevelCodes, opposingTeam]);
    const { data: pitcher, isFetching: isLoadingPitcher } = useCachedOktaQuery(
        ['player', pitcherId],
        (t) => Player.get(t, { playerId: pitcherId }),
    );

    // eslint-disable-next-line no-nested-ternary
    const isLhb = handedness?.value === 3 ? false : handedness?.value === 2 ? true : isLhbFunc(pitcher, batter);
    const levelId = opposingTeam.LevelID !== 8 ? 7 : 8;
    const isVsBatter = handedness?.value === 1;
    const usesYearFilter = window.$IsDugoutApp;

    const heatmapYear = usesYearFilter ? year?.value : (new Date(endDate)).getFullYear();

    const { data: overviewHeatmaps, isLoading: isLoadingOverviewHeatmaps } = useOktaQuery(
        ['overview-heatmaps', batter.PlayerID, pitcherId],
        (t) => Advance.overview(t, { batterId: batter.PlayerID, pitcherId, teamId: resolvedUserTeamId, vsTeamId: opposingTeam.TeamID }),
    );
    const { data: heatmapImages, isLoading: isLoadingHeatmapImages } = useOktaQuery(
        ['catcher-wristband', pitcherId, batter.PlayerID, heatmapYear, resolvedUserTeamId, opposingTeam],
        (t) => HeatMapsApi.catcherWristband(t, pitcherId, batter.PlayerID, heatmapYear, { nyyteamid: resolvedUserTeamId, vsteamid: opposingTeam.TeamID }),
        { enabled: isVsBatter },
    );
    const { data: reportYearsPitching, isLoading: isLoadingReportYears } = useOktaQuery(
        ['reportyearspitching', pitcherId],
        (t) => Player.getReportYearsPitching(t, pitcherId),
        { enabled: !!pitcherId },
    );
    const pitcherOverviewParams = useMemo(() => ({
        pitcherId,
        batterId: levelId === 8 && isVsBatter ? batter.PlayerID : null,
        levelId,
        yearId: usesYearFilter ? year?.value : null,
        isLhb: isVsBatter ? null : isLhb,
        startDate: usesYearFilter ? null : moment(startDate).format('YYYY-MM-DD'),
        endDate: usesYearFilter ? null : moment(endDate).format('YYYY-MM-DD'),
        levelcode: isVsBatter ? null : resolvedLevelcode,
    }), [pitcherId, batter.PlayerID, levelId, year, isLhb, startDate, endDate, resolvedLevelcode, isVsBatter, usesYearFilter]);
    const { data: pitcherOverview, isLoading: isLoadingPitcherOverview, isFetched } = useOktaQuery(
        ['pitcherOverview', pitcherOverviewParams],
        (t) => Advance.pitcherOverview(t, pitcherOverviewParams),
        { enabled: !!pitcher && !!batter && !!handedness && (!usesYearFilter || !!year) },
    );
    const { data: pitchCharacteristics, isLoading: isLoadingPitchCharacteristics } = useOktaQuery(
        ['pitchCharacteristics', pitcherId],
        (t) => PlayerReport.pitchcharacteristics(t, { playerId: pitcherId, mergeFb: false }),
    );
    // eslint-disable-next-line no-nested-ternary
    const damageZonesHeatMapPlayerId = handedness?.value === 1 ? batter.PlayerID : (isLhb ? 2 : 1);
    const { data: damageZonesHeatMap, isLoading: isLoadingDamageZonesHeatMap } = useOktaQuery(
        ['damageZonesHeatMap', damageZonesHeatMapPlayerId],
        (t) => HeatMapsApi.damageZones(t, damageZonesHeatMapPlayerId, { yearId: -2 }),
    );
    const { data: heatmaps, isLoading: isLoadingHeatmaps } = useOktaQuery(
        ['heatmaps-pitchlocationactual', pitcherId, heatmapYear],
        (t) => HeatMapsApi.pitchLocationActual(t, pitcherId, { yearId: heatmapYear }),
        { enabled: !!heatmapYear },
    );
    const { data: zScoreData, isLoading: isLoadingZScoreData } = useOktaQuery(
        ['zScoreData', { isLhb, isLhp: pitcher?.Throws === 2, levelid: levelId, yearid: -2 }],
        (t) => Stats.pitchtypebreakdownpitchfxzscore(t, { islhb: isLhb, islhp: pitcher.Throws === 2, levelid: levelId, yearid: -2 }),
        { enabled: !!pitcher },
    );
    const timeThroughOrderParams = useMemo(() => ({
        pitcherId,
        yearId: usesYearFilter ? year?.value : null,
        levelId,
        isLhb,
        levelcode: resolvedLevelcode,
        startDate: usesYearFilter ? null : moment(startDate).format('YYYY-MM-DD'),
        endDate: usesYearFilter ? null : moment(endDate).format('YYYY-MM-DD'),
    }), [pitcherId, levelId, resolvedLevelcode, isLhb, year, startDate, endDate, usesYearFilter]);
    const { data: timeThroughOrder, isLoading: isLoadingTimeThroughOrder } = useOktaQuery(
        ['timethroughorder', timeThroughOrderParams],
        (t) => PlayerReport.timethroughorder(t, timeThroughOrderParams),
        { enabled: !!pitcher && !!batter && !!handedness && (!usesYearFilter || !!year) },
    );

    const { splits, repertoire, lgavgkzon, pitches, kzon, expected, pitchtypeaverages, hbatvbatellipses } = pitcherOverview || {};
    const numFbInRepertoire = repertoire?.reduce((all, stat) => all + ([9, 10].includes(stat.fxtype) ? 1 : 0), 0);
    const attackHeatmapsTitle = [
        translate('sections.opponent.player.attack-counts'),
        `/ 2-${translate('baseball.strikes_one')}`,
        translate('baseball.count'),
    ].join(' ');

    if (isLoadingPitcher) {
        return <div className="py-5 text-center"> <LoadingSpinner /> </div>;
    }
    const exportReady = !(isLoadingPitcherOverview
        || isLoadingPitchCharacteristics
        || !isFetched
        || isLoadingReportYears
        || isLoadingDamageZonesHeatMap
        || isLoadingHeatmaps
        || isLoadingZScoreData
        || isLoadingTimeThroughOrder
        || (isVsBatter && (isLoadingOverviewHeatmaps || isLoadingHeatmapImages)));

    return (
        <StyledContainer
            data-export-report="pitcher-overview"
            data-export-ready={exportReady ? 'true' : 'false'}
            data-export-report-batter-id={batter.PlayerID}
            data-export-report-pitcher-id={pitcherId}
            data-export-report-handedness={handedness?.value}
        >
            <Row className="align-items-center">
                <Col md={6} sm={12}>
                    <PitcherHeader
                        pitcher={pitcher}
                        isLhb={isLhb}
                        splits={splits}
                        batter={handedness?.value === 1 ? batter : null}
                        levelId={opposingTeam.LevelID}
                    />
                </Col>
                <Col md={6} sm={12}>
                    <Filters
                        key={pitcherId}
                        pitcherId={pitcherId}
                        isLhb={isLhb}
                        year={year}
                        setYear={setYear}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        handedness={handedness}
                        setHandedness={setHandedness}
                        reportYearsPitching={reportYearsPitching}
                        pitcherLevelCodes={pitcherLevelCodes}
                        setPitcherLevelCodes={setPitcherLevelCodes}
                        levels={[...new Set(pitches?.map((x) => x.levelid))]}
                        batter={batter}
                        initialHandedness={initialHandedness}
                    />
                </Col>
            </Row>
            {(isLoadingPitcherOverview || isLoadingPitchCharacteristics || !isFetched || isLoadingReportYears) && (<div className="py-5 text-center"> <LoadingSpinner /> </div>)}
            {!(isLoadingPitcherOverview || isLoadingPitchCharacteristics || !isFetched || isLoadingReportYears) && (
                <>
                    <DeliveryNotes isLhb={isLhb} pitcherId={pitcherId} levelId={levelId} />
                    <Row>
                        <Col sm={12}>
                            <RepertoireTable
                                pitcherId={pitcherId}
                                levelId={levelId}
                                repertoire={repertoire}
                                numFbInRepertoire={numFbInRepertoire}
                                lgavgkzon={lgavgkzon}
                            />
                        </Col>
                    </Row>
                    {isVsBatter && overviewHeatmaps && heatmapImages?.length > 0 && (
                        <ReportCard title={attackHeatmapsTitle}>
                            <PitcherOverviewAttack2KHeatMaps
                                batterId={batter.PlayerID}
                                repertoire={repertoire}
                                expected={expected}
                                images={heatmapImages}
                                overview={overviewHeatmaps}
                                isLhb={isLhb}
                                translate={translate}
                            />
                        </ReportCard>
                    )}
                    <Row>
                        <Col sm={12}>
                            <PitchFrequencyTable
                                isVsBatter={handedness?.value === 1}
                                expected={expected}
                                kzon={kzon}
                                lgavgkzon={lgavgkzon}
                                numFbInRepertoire={numFbInRepertoire}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <AdvanceReportNotes
                                pitcherId={pitcherId}
                                isLhb={isLhb}
                                levelId={levelId}
                            />
                        </Col>
                    </Row>
                    {levelId !== 8 && (
                        <Row>
                            <Col sm={12}>
                                <StatNotes
                                    pitcherId={pitcherId}
                                    isLhb={isLhb}
                                    levelId={levelId}
                                />
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col sm={12}>
                            <PitchLocations
                                modalContainer={modalContainer}
                                isLhb={isLhb}
                                isLhp={pitcher.Throws === 2}
                                levelId={levelId}
                                pitches={pitches}
                                repertoire={repertoire}
                                yearId={heatmapYear}
                                timeThroughOrder={timeThroughOrder?.repertoire}
                                isStartingPitcher={pitcher?.PrimaryPosition === 11 || pitcher?.IsTwoWay === true}
                                szTop={batter?.SzTop}
                                szBottom={batter?.SzBottom}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <HeatMaps
                                isLoading={isLoadingHeatmaps}
                                heatmaps={heatmaps}
                                yearId={heatmapYear}
                                isLhb={isLhb}
                                pitches={pitches}
                                repertoire={repertoire}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <PitchBreak
                                isVsBatter={handedness?.value === 1}
                                pitches={pitches}
                                pitchCharacteristics={pitchCharacteristics}
                                throws={pitcher.Throws}
                                pitcherId={pitcherId}
                                yearId={null}
                                isLhb={isLhb}
                                isLhp={pitcher.Throws === 2}
                                levelId={levelId}
                                lgavgkzon={lgavgkzon}
                                repertoire={repertoire}
                                isLoading={isLoadingZScoreData}
                                zScoreData={zScoreData}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <BatTracking
                                pitches={pitchtypeaverages}
                                isLhb={isLhb}
                                isLhp={pitcher.Throws === 2}
                                repertoire={repertoire}
                                hbatVbatEllipses={hbatvbatellipses}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <PitchUsageTable
                                isLoading={isLoadingTimeThroughOrder}
                                timeThroughOrder={timeThroughOrder}
                                lgavgkzon={lgavgkzon}
                                numFbInRepertoire={numFbInRepertoire}
                                isStartingPitcher={pitcher?.PrimaryPosition === 11 || pitcher?.IsTwoWay === true}
                                repertoire={repertoire}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <TrendsChart
                                pitches={pitches}
                                isStartingPitcher={pitcher?.PrimaryPosition === 11 || pitcher?.IsTwoWay === true}
                                repertoire={repertoire}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <BarrelBoxes
                                isLhb={isLhb}
                                pitcherId={pitcherId}
                                levelId={levelId}
                            />
                        </Col>
                    </Row>
                    <AttackPlanNotes isLhb={isLhb} pitcherId={pitcherId} levelId={levelId} />
                    <Row>
                        <Col sm={12}>
                            <DamageZones
                                isLoading={isLoadingDamageZonesHeatMap}
                                isLhp={pitcher.Throws === 2}
                                isLhb={isLhb}
                                repertoire={repertoire}
                                pitches={pitches}
                                damageZonesHeatMap={damageZonesHeatMap}
                                szTop={batter?.SzTop}
                                szBottom={batter?.SzBottom}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <SprayChart isLhb={isLhb} pitches={pitches} />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12}>
                            <PitchMovementImages
                                levelId={opposingTeam.LevelIDData}
                                pitcherId={pitcherId}
                                repertoire={repertoire}
                                pitchCharacteristics={pitchCharacteristics}
                                isLhp={pitcher.Throws === 2}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </StyledContainer>
    );
};

export default PitcherOverview;
